import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { withStyles } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';

import AuthService from "../../../services/AuthService";
import { useNotificationContext } from "../../../services/NotificationService";
import styles from './styles';

const ResetPassword = (props) => {
    const { classes } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const notifications = useNotificationContext();
    const resetToken = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
    const isConfirmMode = Boolean(resetToken);

    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    useEffect(() => {
        return () => {
            notifications.dispatch({
                type: "HIDE_ALERT"
            })
        }
    }, []);

    const handleResetPasswordClick = () => {
        AuthService.ResetPassword(email)
            .then(function (response) {
                notifications.dispatch({
                    type: "DISPLAY_ALERT",
                    payload: { "message": response?.data?.message || "If an account exists for that email, reset instructions have been sent.", "severity": "info" }
                })
            })
            .catch(function (error) {
                notifications.dispatch({
                    type: "DISPLAY_ALERT",
                    payload: { "message": error?.response?.data?.detail || "Unable to start password reset.", "severity": "error" }
                })
            })
    };

    const handleConfirmResetPasswordClick = () => {
        if (newPassword !== confirmPassword) {
            notifications.dispatch({
                type: "DISPLAY_ALERT",
                payload: { "message": "New passwords do not match.", "severity": "error" }
            });
            return;
        }

        AuthService.ConfirmResetPassword(resetToken, newPassword)
            .then(function (response) {
                notifications.dispatch({
                    type: "DISPLAY_ALERT",
                    payload: { "message": response?.data?.message || "Password changed successfully.", "severity": "success" }
                });
                navigate("/login");
            })
            .catch(function (error) {
                notifications.dispatch({
                    type: "DISPLAY_ALERT",
                    payload: { "message": error?.response?.data?.detail || "Unable to reset password.", "severity": "error" }
                });
            });
    };

    return (
        <div className={classes.root}>
            <main className={classes.main}>
                <Container maxWidth="sm">
                    <Card>
                        <CardContent>
                            <Typography variant="h4" gutterBottom>
                                {isConfirmMode ? "Choose a New Password" : "Reset Password"}
                            </Typography>
                            <Typography color="textSecondary">
                                {isConfirmMode
                                    ? "Enter a new password for your account."
                                    : "Enter your account email and we will send you a reset link."}
                            </Typography>
                            {!isConfirmMode && (
                                <div className={classes.inputContainer}>
                                    <TextField
                                        label="Email"
                                        placeholder="Email"
                                        helperText=""
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        variant="outlined"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                            )}
                            {isConfirmMode && (
                                <React.Fragment>
                                    <div className={classes.inputContainer}>
                                        <TextField
                                            label="New Password"
                                            placeholder="Enter a new password"
                                            helperText=""
                                            fullWidth
                                            margin="normal"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            variant="outlined"
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </div>
                                    <div className={classes.inputContainer}>
                                        <TextField
                                            label="Confirm Password"
                                            placeholder="Re-enter your new password"
                                            helperText=""
                                            fullWidth
                                            margin="normal"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            variant="outlined"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                    </div>
                                </React.Fragment>
                            )}
                            <div className={classes.buttonContainer}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => isConfirmMode ? handleConfirmResetPasswordClick() : handleResetPasswordClick()}
                                >
                                    {isConfirmMode ? "Save New Password" : "Send Reset Link"}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </Container>
            </main>
        </div>
    );
};

export default withStyles(styles)(ResetPassword);
